import React, { useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Link,
  IconButton,
  Divider,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Stack
} from '@mui/material';
import {
  Facebook,
  Instagram,
  Twitter,
  YouTube,
  Phone,
  Email,
  LocationOn,
  WhatsApp
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { usePageSections } from '../../contexts/PageSectionsContext';
import { useTranslation } from 'react-i18next';
import { getTranslatedData } from '../../utils/getTranslatedData';

const Footer: React.FC = () => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { footerData, loading } = usePageSections();
  const selectedLang = i18n.language;

  // Fallback data eğer dinamik veri yüklenmediyse
  const fallbackData = {
    companyInfo: {
      name: 'HatTravel',
      description: 'Türkiye\'nin önde gelen seyahat acentesi olarak, unutulmaz deneyimler yaşatmak için çalışıyoruz. Güvenilir, kaliteli ve uygun fiyatlı tur paketlerimizle hayalinizdeki tatili gerçekleştirin.'
    },
    contactInfo: {
      phones: ['+90 555 123 45 67'],
      emails: ['info@hattravel.com'],
      address: 'Hatay, Türkiye',
      whatsapp: '+90 555 123 45 67'
    },
    socialMedia: {
      facebook: 'https://facebook.com/hattravel',
      instagram: 'https://instagram.com/hattravel',
      twitter: 'https://twitter.com/hattravel',
      youtube: 'https://youtube.com/hattravel',
      whatsapp: 'https://wa.me/905551234567'
    },
    quickLinks: [
      { id: '1', text: 'Ana Sayfa', path: '/', order: 1, isActive: true },
      { id: '2', text: 'Tur Paketleri', path: '/tours', order: 2, isActive: true },
      { id: '3', text: 'Blog', path: '/blog', order: 3, isActive: true },
      { id: '4', text: 'Hakkımızda', path: '/about', order: 4, isActive: true },
      { id: '5', text: 'İletişim', path: '/contact', order: 5, isActive: true }
    ],
    supportLinks: [
      { id: '1', text: 'Sık Sorulan Sorular', path: '/faq', order: 1, isActive: true },
      { id: '2', text: 'Rezervasyon Koşulları', path: '/booking-terms', order: 2, isActive: true },
      { id: '3', text: 'Gizlilik Politikası', path: '/privacy-policy', order: 3, isActive: true },
      { id: '4', text: 'İptal ve İade', path: '/cancellation-refund', order: 4, isActive: true },
      { id: '5', text: 'Yardım Merkezi', path: '/help-center', order: 5, isActive: true }
    ],
    bottomLinks: [
      { id: '1', text: 'Rezervasyon Koşulları', path: '/booking-terms', order: 1, isActive: true },
      { id: '2', text: 'Gizlilik Politikası', path: '/privacy-policy', order: 2, isActive: true },
      { id: '3', text: 'İptal ve İade', path: '/cancellation-refund', order: 3, isActive: true }
    ],
    copyright: `© ${new Date().getFullYear()} HatTravel. Tüm hakları saklıdır.`
  };

  // Dinamik veri varsa onu kullan, yoksa fallback'i kullan
  const data = footerData || fallbackData;

  // Aktif linkleri filtrele ve sırala
  const isActiveUnlessFalse = (link: any) => link.isActive !== false;
  const activeQuickLinks = (data.quickLinks?.length ? data.quickLinks : fallbackData.quickLinks).filter(isActiveUnlessFalse).sort((a,b)=> (a.order??0)-(b.order??0));
  const activeSupportLinks = (data.supportLinks?.length ? data.supportLinks : fallbackData.supportLinks).filter(isActiveUnlessFalse).sort((a,b)=> (a.order??0)-(b.order??0));
  const activeBottomLinks = (data.bottomLinks?.length ? data.bottomLinks : fallbackData.bottomLinks).filter(isActiveUnlessFalse).sort((a,b)=> (a.order??0)-(b.order??0));

  const footerSections = [
    {
      title: t('footer.quick_links'),
      links: activeQuickLinks
    },
    {
      title: t('footer.support_help'),
      links: activeSupportLinks
    }
  ];

  const socialLinks = [
    { icon: <Facebook />, url: data.socialMedia?.facebook, label: 'Facebook' },
    { icon: <Instagram />, url: data.socialMedia?.instagram, label: 'Instagram' },
    { icon: <Twitter />, url: data.socialMedia?.twitter, label: 'Twitter' },
    { icon: <YouTube />, url: data.socialMedia?.youtube, label: 'YouTube' },
    { icon: <WhatsApp />, url: data.socialMedia?.whatsapp, label: 'WhatsApp' },
  ].filter(social => social.url); // Sadece URL'si olan sosyal medya linklerini göster

  useEffect(() => {
    // ... existing code ...
  }, []);

  if (loading) {
    return (
      <Box
        component="footer"
        sx={{
          background: 'var(--navy-blue)',
          color: 'var(--light-gray-white)',
          mt: 'auto',
          py: 6,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '200px'
        }}
      >
        <CircularProgress sx={{ color: 'var(--light-blue-cta)' }} />
      </Box>
    );
  }

  return (
    <Box
      component="footer"
      sx={{
        background: 'var(--navy-blue)',
        color: 'var(--light-gray-white)',
        mt: 'auto',
        py: 6,
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '1px',
          background: 'linear-gradient(90deg, transparent 0%, var(--gray-shadow) 50%, transparent 100%)'
        }
      }}
    >
      <Container maxWidth="xl">
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr 1fr 1fr' }, gap: 4 }}>
          {/* Company Info */}
          <Box>
            <Typography 
              variant="h5" 
              component="h2" 
              gutterBottom 
              sx={{ 
                fontWeight: 700,
              }}
            >
              {getTranslatedData(data.companyInfo?.name) || 'HatTravel'}
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, opacity: 0.8, lineHeight: 1.6 }}>
              {getTranslatedData(data.companyInfo?.description)}
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
              {socialLinks.map((social) => (
                <IconButton
                  key={social.label}
                  component="a"
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: 'var(--light-gray-white)',
                    backgroundColor: 'transparent',
                    border: '1px solid var(--gray-shadow)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: 'var(--cobalt-blue)',
                      borderColor: 'var(--cobalt-blue)',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  {social.icon}
                </IconButton>
              ))}
            </Box>
          </Box>

          {/* Footer Sections */}
          {footerSections.map((section) => (
            <Box key={section.title}>
              <Typography 
                variant="h6" 
                component="h3" 
                gutterBottom 
                sx={{ 
                  fontWeight: 600,
                  color: 'var(--light-blue-cta)',
                  mb: 3
                }}
              >
                {section.title}
              </Typography>
              <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                {section.links.map((link) => (
                  <Box component="li" key={link.id} sx={{ mb: 2 }}>
                    <Link
                      component={RouterLink}
                      to={link.path}
                      sx={{
                        color: 'var(--light-gray-white)',
                        textDecoration: 'none',
                        opacity: 0.8,
                        transition: 'all 0.3s ease',
                        fontSize: '0.9rem',
                        '&:hover': {
                          opacity: 1,
                          color: 'var(--cobalt-blue)',
                          transform: 'translateX(4px)',
                        },
                      }}
                    >
                      {getTranslatedData(link.text)}
                    </Link>
                  </Box>
                ))}
              </Box>
            </Box>
          ))}

          {/* Contact Info */}
          <Box>
            <Typography 
              variant="h6" 
              component="h3" 
              gutterBottom 
              sx={{ 
                fontWeight: 600,
                color: 'var(--light-blue-cta)',
                mb: 3
              }}
            >
              {t('footer.contact')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {data.contactInfo?.phones?.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Phone sx={{ fontSize: 20, opacity: 0.8, color: 'var(--light-blue-cta)' }} />
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    {getTranslatedData(data.contactInfo.phones[0], selectedLang)}
                  </Typography>
                </Box>
              )}
              {data.contactInfo?.emails?.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Email sx={{ fontSize: 20, opacity: 0.8, color: 'var(--light-blue-cta)' }} />
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    {getTranslatedData(data.contactInfo.emails[0], selectedLang)}
                  </Typography>
                </Box>
              )}
              {data.contactInfo?.address && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <LocationOn sx={{ fontSize: 20, opacity: 0.8, mt: 0.5, color: 'var(--light-blue-cta)' }} />
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    {getTranslatedData(data.contactInfo.address, selectedLang)}
                  </Typography>
                </Box>
              )}
              {data.contactInfo?.whatsapp && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <WhatsApp sx={{ fontSize: 20, opacity: 0.8, mt: 0.2, color: 'var(--light-blue-cta)' }} />
                  <Typography variant="body2" sx={{ opacity: 0.8, whiteSpace: 'nowrap' }}>
                    WhatsApp: {getTranslatedData(data.contactInfo.whatsapp, selectedLang)}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 4, borderColor: 'var(--gray-shadow)' }} />

        {/* Bottom Footer */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between',
            alignItems: isMobile ? 'flex-start' : 'center',
            gap: 2,
          }}
        >
          <Typography variant="body2" sx={{ opacity: 0.7 }}>
            {getTranslatedData(data.copyright)}
          </Typography>
          
          
        </Box>
      </Container>
    </Box>
  );
};

export default Footer; 